import React, { useEffect, useRef } from 'react';
import RecommendationServiceCarouselItem from './RecommendationServiceListItem';
import type { RSProducts } from './RecommendationServiceTypes';
import { scrollOneScreen, handleSlider, throttledHandleSlider } from '../helpers';
import { useMediaQuery } from '@material-ui/core';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import { heapTrack } from 'src/scripts/utils/helpers';
import { fireWidgetViewPixel } from 'src/scripts/utils/bloomreach-recommendations';
import { useIntersectionObserver } from 'src/scripts/templates/_shared/react-hooks';

import './../../../../_shared/ui-components/PairsGreatWith/styles.scss';
import { getGatedSaleProperties } from 'src/scripts/utils/gatedSaleUtils';

const firedViewPixels = new Set<string>();

const RecommendationServiceCarousel = ({ data, blockTitle, brRecommendationMetadata }: RSProducts) => {
  const { recommendedProductsByProductId: products } = data;
  const carousel: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
  const productSlider: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });

  useEffect(() => {
    heapTrack(
      blockTitle === 'Similar Items' ? 'PDP - Visit - Rec Service Carousel' : `PDP - Visit - ${blockTitle} Carousel`,
      { status: 'rendered' }
    );
  }, [blockTitle]);

  const { isIntersecting } = useIntersectionObserver(carousel);
  useEffect(() => {
    const brWidgetRID = brRecommendationMetadata?.widget?.rid ?? 'br_ext';
    const pixelKey = `${blockTitle}:${brWidgetRID}`;
    if (!isIntersecting || firedViewPixels.has(pixelKey)) return;

    firedViewPixels.add(pixelKey);
    fireWidgetViewPixel({
      wrid: brWidgetRID,
      wid: brRecommendationMetadata?.widget?.id ?? 'br_ext',
      wty: brRecommendationMetadata?.widget?.type ?? 'br_ext',
    });
  }, [brRecommendationMetadata, blockTitle, isIntersecting]);

  useEffect(() => {
    const currentSlider = productSlider.current;
    if (currentSlider && !isMobile) {
      handleSlider(carousel);
      currentSlider.addEventListener('scroll', () => {
        throttledHandleSlider(carousel);
      });
      return () => {
        currentSlider.removeEventListener('scroll', () => {
          throttledHandleSlider(carousel);
        });
      };
    }
  }, [isMobile]);

  const { containerClassName, badgeStyle } = getGatedSaleProperties();

  return products.length >= 1 ? (
    <div
      ref={carousel}
      className={`container product-carousel-container crosssell-product-container position-relative product-carousel ${containerClassName}`}
      style={badgeStyle}
    >
      <div className="row no-gutters">
        <div className="col-12">
          <h4 className="similar-items__title h4 semibold black uppercase">
            {blockTitle ? blockTitle : 'Pairs Great With'}
          </h4>
        </div>
      </div>
      <div
        className="arrow--left insta-arrow--left"
        role="button"
        onClick={() => {
          if (productSlider) scrollOneScreen('left', productSlider);
        }}
      />
      <div
        className="arrow--right insta-arrow--right"
        role="button"
        onClick={() => {
          if (productSlider) scrollOneScreen('right', productSlider);
        }}
      />

      <div ref={productSlider} className="row no-gutters product-carousel--pdp crosssell-product--pdp">
        {products.map((product) => (
          <RecommendationServiceCarouselItem
            data={product}
            key={product.handle}
            blockTitle={blockTitle}
            brRecommendationMetadata={brRecommendationMetadata}
          />
        ))}
      </div>
    </div>
  ) : null;
};

export default RecommendationServiceCarousel;
